import { useAuth } from '~/composables/useAuth';
import type { OrgInfoApiResponse } from '~/types';
import { Role } from '~/types/auth';
import { getCachedOrgInfo } from '~/utils/auth/orgInfoCache';

const permissionConfig = new Map([
  [
    '/donations',
    {
      editActionButton: [Role.Admin, Role.DonationManager],
      mailActionButton: [Role.Admin, Role.DonationManager, Role.DonorManager],
      whatsappActionButton: [Role.Admin, Role.DonationManager, Role.DonorManager],
      receiptActionButton: [Role.Admin, Role.DonationManager, Role.DonorManager],
      deleteActionButton: [Role.Admin, Role.DonationManager],
      viewActionButton: [Role.Admin, Role.DonationManager, Role.DonorManager, Role.ReadOnly, Role.Auditor],
      duplicateActionButton: [Role.Admin, Role.DonationManager],
      headerPrimaryButton: [Role.Admin, Role.DonationManager],
      editLastSentButton: [Role.Admin, Role.DonationManager],
      headerExportButton: [Role.Admin, Role.DonationManager, Role.DonorManager, Role.ReadOnly, Role.Auditor]
    }
  ],
  [
    '/donations/add-new-donation',
    {
      headerPrimaryButton: [Role.Admin, Role.DonationManager]
    }
  ],
  [
    '/donations/edit-donation',
    {
      headerPrimaryButton: [Role.Admin, Role.DonationManager]
    }
  ],
  [
    '/donors',
    {
      headerPrimaryButton: [Role.Admin, Role.DonorManager],
      headerExportButton: [Role.Admin, Role.DonorManager, Role.DonationManager, Role.Auditor],
      editActionButton: [Role.Admin, Role.DonorManager],
      deleteActionButton: [Role.Admin, Role.DonorManager],
      viewActionButton: [Role.Admin, Role.DonorManager, Role.DonationManager, Role.ReadOnly, Role.Auditor],
      addDonationActionButton: [Role.Admin, Role.DonationManager, Role.Auditor],
      addPledgeActionButton: [Role.Admin, Role.DonationManager, Role.Auditor],
      addGrantActionButton: [Role.Admin, Role.DonationManager, Role.Auditor]
    }
  ],
  [
    '/donors/add-new-donor',
    {
      headerPrimaryButton: [Role.Admin, Role.DonorManager]
    }
  ],
  [
    '/donors/edit-donor',
    {
      headerPrimaryButton: [Role.Admin, Role.DonorManager],
      editActionButton: [Role.Admin, Role.DonorManager],
      viewActionButton: [Role.Admin, Role.DonorManager, Role.ReadOnly, Role.DonationManager, Role.Auditor]
    }
  ],

  [
    '/payees',
    {
      headerPrimaryButton: [Role.Admin, Role.VendorSubmitter, Role.VendorAdmin, Role.PayeeSubmitter, Role.PayeeAdmin],
      headerExportButton: [
        Role.Admin,
        Role.ReadOnly,
        Role.VendorApprover,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ],
      viewActionButton: [
        Role.Admin,
        Role.ReadOnly,
        Role.VendorApprover,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ],
      editActionButton: [
        Role.Admin,
        Role.VendorApprover,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ],
      approverVendorButton: [
        Role.Admin,
        Role.VendorApprover,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ],
      rejectVendorButton: [
        Role.Admin,
        Role.VendorApprover,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ],
      editPayeeButton: [
        Role.Admin,
        Role.VendorApprover,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ],
      deactivateVendorButton: [
        Role.Admin,
        Role.VendorApprover,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ],
      submitVendorButton: [
        Role.Admin,
        Role.VendorApprover,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ]
    }
  ],
  [
    '/payees/add-new-payee',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.VendorApprover,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ]
    }
  ],
  [
    '/payees/edit-payee',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.VendorApprover,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ]
    }
  ],
  [
    '/advances',
    {
      headerPrimaryButton: [Role.Admin, Role.AdvanceSubmitter, Role.AdvanceAdmin],
      headerExportButton: [Role.Admin, Role.AdvanceSubmitter, Role.AdvanceApprover, Role.AdvanceAdmin, Role.ReadOnly],
      viewActionButton: [Role.Admin, Role.AdvanceSubmitter, Role.AdvanceApprover, Role.AdvanceAdmin, Role.ReadOnly],
      editActionButton: [Role.Admin, Role.AdvanceSubmitter, Role.AdvanceApprover, Role.AdvanceAdmin],
      creditCardButton: [Role.Admin, Role.AdvanceSubmitter, Role.AdvanceApprover, Role.AdvanceAdmin],
      updatePaymentActionButton: [Role.Admin, Role.AdvanceSubmitter, Role.AdvanceApprover, Role.AdvanceAdmin],
      approveActionButton: [Role.Admin, Role.AdvanceApprover, Role.AdvanceAdmin],
      closeActionButton: [Role.Admin, Role.AdvanceApprover, Role.AdvanceAdmin],
      cancelActionButton: [Role.Admin, Role.AdvanceSubmitter, Role.AdvanceApprover, Role.AdvanceAdmin]
    }
  ],
  [
    '/advances/add-new-advance',
    {
      headerPrimaryButton: [Role.Admin, Role.AdvanceSubmitter, Role.AdvanceApprover, Role.AdvanceAdmin]
    }
  ],
  [
    '/advances/edit-advance',
    {
      headerPrimaryButton: [Role.Admin, Role.AdvanceSubmitter, Role.AdvanceApprover, Role.AdvanceAdmin]
    }
  ],
  [
    '/grants',
    {
      headerPrimaryButton: [Role.Admin, Role.GrantAdmin, Role.GrantSubmitter],
      viewActionButton: [
        Role.Admin,
        Role.GrantAdmin,
        Role.GrantSubmitter,
        Role.GrantMember,
        Role.ReadOnly,
        Role.Auditor,
        Role.DonorAuditor
      ],
      editActionButton: [Role.Admin, Role.GrantSubmitter, Role.GrantAdmin, Role.GrantMember],
      duplicateActionButton: [Role.Admin, Role.GrantAdmin, Role.GrantSubmitter],
      closeActionButton: [Role.Admin, Role.GrantAdmin, Role.GrantSubmitter, Role.GrantMember],
      headerExportButton: [
        Role.Admin,
        Role.GrantSubmitter,
        Role.GrantMember,
        Role.ReadOnly,
        Role.Auditor,
        Role.DonorAuditor,
        Role.GrantAdmin
      ]
    }
  ],
  [
    '/grants/add-new-grant',
    {
      headerPrimaryButton: [Role.Admin, Role.GrantAdmin, Role.GrantSubmitter]
    }
  ],
  [
    '/grants/edit-grant',
    {
      headerPrimaryButton: [Role.Admin, Role.GrantAdmin, Role.GrantSubmitter, Role.GrantMember],
      closeActionButton: [Role.Admin, Role.GrantAdmin, Role.GrantSubmitter, Role.GrantMember]
    }
  ],
  [
    '/pledges',
    {
      headerPrimaryButton: [Role.Admin, Role.PledgeManager, Role.DonorManager],
      duplicateActionButton: [Role.Admin, Role.PledgeManager, Role.DonorManager],
      editActionButton: [Role.Admin, Role.PledgeManager, Role.DonorManager],
      viewActionButton: [
        Role.Admin,
        Role.PledgeManager,
        Role.DonorManager,
        Role.DonationManager,
        Role.ReadOnly,
        Role.Auditor
      ],
      mailActionButton: [Role.Admin, Role.PledgeManager, Role.DonorManager],
      whatsappActionButton: [Role.Admin, Role.PledgeManager, Role.DonorManager],
      closeActionButton: [Role.Admin, Role.PledgeManager, Role.DonorManager],
      headerExportButton: [Role.Admin, Role.PledgeManager, Role.DonorManager, Role.ReadOnly, Role.Auditor]
    }
  ],
  [
    '/pledges/add-new-pledge',
    {
      headerPrimaryButton: [Role.Admin, Role.DonorManager, Role.DonationManager]
    }
  ],
  [
    '/pledges/edit-pledge',
    {
      headerPrimaryButton: [Role.Admin, Role.DonorManager, Role.DonationManager],
      closeActionButton: [Role.Admin, Role.DonorManager, Role.DonationManager]
    }
  ],
  [
    '/budgets',
    {
      approveActionButton: [Role.Admin, Role.BudgetAdmin, Role.BudgetApprover],
      headerPrimaryButton: [Role.Admin, Role.BudgetAdmin, Role.BudgetSubmitter],
      editActionButton: [Role.Admin, Role.BudgetAdmin, Role.BudgetApprover, Role.BudgetMember, Role.BudgetSubmitter],
      closeActionButton: [Role.Admin, Role.BudgetAdmin, Role.BudgetApprover, Role.BudgetMember, Role.BudgetSubmitter],
      viewActionButton: [
        Role.Admin,
        Role.ReadOnly,
        Role.Auditor,
        Role.DonorAuditor,
        Role.BudgetApprover,
        Role.BudgetAdmin,
        Role.BudgetMember,
        Role.BudgetSubmitter
      ],
      duplicateActionButton: [
        Role.Admin,
        Role.BudgetAdmin,
        Role.BudgetApprover,
        Role.BudgetMember,
        Role.BudgetSubmitter
      ],
      headerExportButton: [
        Role.Admin,
        Role.BudgetAdmin,
        Role.BudgetApprover,
        Role.BudgetMember,
        Role.ReadOnly,
        Role.Auditor,
        Role.DonorAuditor
      ],
      submitActionButton: [Role.Admin, Role.BudgetAdmin, Role.BudgetSubmitter, Role.BudgetApprover, Role.BudgetMember]
    }
  ],
  [
    '/budgets/add-new-budget',
    {
      headerPrimaryButton: [Role.Admin, Role.BudgetAdmin, Role.BudgetSubmitter, Role.BudgetApprover, Role.BudgetMember]
    }
  ],
  [
    '/budgets/edit-budget',
    {
      headerPrimaryButton: [Role.Admin, Role.BudgetAdmin, Role.BudgetSubmitter, Role.BudgetApprover, Role.BudgetMember]
    }
  ],
  [
    '/admin',
    {
      headerPrimaryButton: [Role.Admin]
    }
  ],
  [
    '/admin/users',
    {
      headerPrimaryButton: [Role.Admin],
      addActionButton: [Role.Admin],
      viewActionButton: [Role.Admin],
      editActionButton: [Role.Admin],
      duplicateActionButton: [Role.Admin],
      headerExportButton: [Role.Admin]
    }
  ],
  [
    '/admin/add_new_user',
    {
      headerPrimaryButton: [Role.Admin]
    }
  ],
  [
    '/admin/edit_user',
    {
      headerPrimaryButton: [Role.Admin]
    }
  ],
  [
    '/admin/audit_logs_list',
    {
      headerExportButton: [Role.Admin]
    }
  ],
  [
    '/setup/accounting_periods',
    {
      headerPrimaryButton: [Role.Admin],
      headerExportButton: [Role.Admin],
      viewActionButton: [Role.Admin, Role.ReadOnly],
      addActionButton: [Role.Admin],
      editActionButton: [Role.Admin],
      duplicateActionButton: [Role.Admin]
    }
  ],

  [
    '/utilities',
    {
      importActionButton: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    '/utilities/import_donations',
    {
      importActionButton: [Role.Admin, Role.UtilitiesAccess],
      downloadActionButton: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    '/utilities/import_payee',
    {
      importActionButton: [Role.Admin, Role.UtilitiesAccess],
      downloadActionButton: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    '/utilities/import_ledgers',
    {
      importActionButton: [Role.Admin, Role.UtilitiesAccess],

      downloadActionButton: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    '/utilities/import_payments',
    {
      importActionButton: [Role.Admin, Role.UtilitiesAccess],
      downloadActionButton: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    '/utilities/statutory_reports',
    {
      exportActionButton: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    '/utilities/form113',
    {
      exportActionButton: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    '/utilities/10be_emailer',
    {
      importActionButton: [Role.Admin, Role.UtilitiesAccess],
      downloadActionButton: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    '/utilities/export_tally_income',
    {
      exportActionButton: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    '/utilities/export_tally_expense',
    {
      exportActionButton: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    '/utilities/expense_reconciliation',
    {
      reconcileActionButton: [Role.Admin, Role.UtilitiesAccess],
      viewActionButton: [Role.Admin, Role.UtilitiesAccess],
      editActionButton: [Role.Admin, Role.UtilitiesAccess],
      duplicateActionButton: [Role.Admin]
    }
  ],
  [
    '/utilities/expense_reconciliation_status',
    {
      generateActionButton: [Role.Admin, Role.UtilitiesAccess],
      viewActionButton: [Role.Admin, Role.UtilitiesAccess],
      editActionButton: [Role.Admin, Role.UtilitiesAccess],
      duplicateActionButton: [Role.Admin]
    }
  ],
  [
    '/setup',
    {
      headerPrimaryButton: [Role.Admin],
      viewActionButton: [Role.Admin, Role.ReadOnly],
      addActionButton: [Role.Admin],
      editActionButton: [Role.Admin],
      duplicateActionButton: [Role.Admin],
      headerExportButton: [Role.Admin]
    }
  ],
  [
    '/setup/purposes',
    {
      headerPrimaryButton: [Role.Admin],
      headerExportButton: [Role.Admin],
      viewActionButton: [Role.Admin, Role.ReadOnly],
      addActionButton: [Role.Admin],
      editActionButton: [Role.Admin],
      duplicateActionButton: [Role.Admin]
    }
  ],
  [
    '/setup/stock_items',
    {
      headerPrimaryButton: [Role.Admin],
      headerExportButton: [Role.Admin],
      viewActionButton: [Role.Admin, Role.ReadOnly],
      addActionButton: [Role.Admin],
      editActionButton: [Role.Admin],
      duplicateActionButton: [Role.Admin]
    }
  ],
  [
    '/setup/org_banks',
    {
      headerPrimaryButton: [Role.Admin],
      headerExportButton: [Role.Admin],
      viewActionButton: [Role.Admin, Role.ReadOnly],
      addActionButton: [Role.Admin],
      editActionButton: [Role.Admin],
      duplicateActionButton: [Role.Admin]
    }
  ],
  [
    '/setup/attachment_types',
    {
      headerPrimaryButton: [Role.Admin],
      headerExportButton: [Role.Admin],
      viewActionButton: [Role.Admin, Role.ReadOnly],
      addActionButton: [Role.Admin],
      editActionButton: [Role.Admin],
      duplicateActionButton: [Role.Admin]
    }
  ],
  [
    '/setup/ledgers',
    {
      headerPrimaryButton: [Role.Admin],
      headerExportButton: [Role.Admin],
      viewActionButton: [Role.Admin, Role.ReadOnly],
      addActionButton: [Role.Admin],
      editActionButton: [Role.Admin],
      duplicateActionButton: [Role.Admin]
    }
  ],
  [
    '/setup/cost_centres',
    {
      headerPrimaryButton: [Role.Admin],
      headerExportButton: [Role.Admin],
      viewActionButton: [Role.Admin, Role.ReadOnly],
      addActionButton: [Role.Admin],
      editActionButton: [Role.Admin],
      duplicateActionButton: [Role.Admin]
    }
  ],
  [
    '/setup/payee_types',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.ReadOnly,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.BudgetApprover
      ],
      viewActionButton: [Role.Admin, Role.ReadOnly, Role.ExpenseSubmitter, Role.ExpenseApprover, Role.BudgetApprover],
      addActionButton: [Role.Admin, Role.ReadOnly, Role.ExpenseSubmitter, Role.ExpenseApprover, Role.BudgetApprover],
      editActionButton: [Role.Admin, Role.ReadOnly, Role.ExpenseSubmitter, Role.ExpenseApprover, Role.BudgetApprover],
      duplicateActionButton: [
        Role.Admin,
        Role.ReadOnly,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.BudgetApprover
      ]
    }
  ],
  [
    '/user-vs-grants',
    {
      viewActionButton: [Role.Admin, Role.GrantMember],
      editActionButton: [Role.Admin, Role.GrantMember]
    }
  ],
  [
    '/expenses/add-new-expense',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ]
    }
  ],
  [
    '/expenses',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.DonorAuditor,
        Role.ReadOnly,
        Role.ExpenseAdmin,
        Role.ExpenseSubmitter,
        Role.ExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      duplicateActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseSubmitter,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseAdmin
      ],
      approveActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      creditCardButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      updatePaymentActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      cancelActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      submitForApprovalButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseReportManager,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ]
    }
  ],
  [
    '/expenses/edit-expense',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      submitForApprovalButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseReportManager,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ]
    }
  ],
  [
    '/recurring-expenses',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      duplicateActionButton: [
        Role.Admin,
        Role.ExpenseAdmin,
        Role.ExpenseSubmitter,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.ExpenseAdmin,
        Role.ExpenseSubmitter,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      submitForApprovalButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseReportManager,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ]
    }
  ],
  [
    '/recurring-expenses/add-new-recurring-expense',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ]
    }
  ],
  [
    '/recurring-expenses/edit-recurring-expense',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ]
    }
  ]
]);

const tabPermissionConfig = new Map([
  [
    'default',
    {
      headerPrimaryButton: [Role.Admin, Role.DefaultExpenseApprover, Role.DefaultExpenseSubmitter],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      duplicateActionButton: [Role.Admin, Role.DefaultExpenseSubmitter],
      submitForApprovalButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseReportManager,
        Role.ExpenseAdmin,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      creditCardButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      approveActionButton: [Role.Admin, Role.ExpenseApprover],
      updatePaymentActionButton: [
        Role.Admin,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ]
    }
  ],
  [
    'payroll',
    {
      headerPrimaryButton: [Role.Admin, Role.PayrollExpenseApprover, Role.PayrollExpenseSubmitter],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter
      ],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter
      ],
      duplicateActionButton: [Role.Admin, Role.PayrollExpenseSubmitter],
      submitForApprovalButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseReportManager,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseSubmitter,
        Role.PayrollExpenseApprover
      ],
      creditCardButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      approveActionButton: [Role.Admin, Role.ExpenseApprover],
      updatePaymentActionButton: [
        Role.Admin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter
      ]
    }
  ],
  [
    'custom_exp3',
    {
      headerPrimaryButton: [Role.Admin, Role.CustomExp3ExpenseApprover, Role.CustomExp3ExpenseSubmitter],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.CustomExp3ExpenseAdmin,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      duplicateActionButton: [Role.Admin, Role.ExpenseSubmitter],
      submitForApprovalButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseReportManager,
        Role.ExpenseAdmin,
        Role.ExpenseApprover
      ],
      creditCardButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      approveActionButton: [Role.Admin, Role.ExpenseApprover],
      updatePaymentActionButton: [
        Role.Admin,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter,
        Role.CustomExp3ExpenseAdmin
      ]
    }
  ],
  [
    'all_expenses',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.ExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      duplicateActionButton: [Role.Admin, Role.ExpenseSubmitter, Role.ExpenseAdmin],
      submitForApprovalButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseReportManager,
        Role.ExpenseApprover,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      creditCardButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      approveActionButton: [Role.Admin, Role.ExpenseApprover],
      updatePaymentActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter,
        Role.CustomExp3ExpenseAdmin
      ]
    }
  ],
  [
    'pending_approval_by_me',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      duplicateActionButton: [
        Role.Admin,
        Role.ExpenseAdmin,
        Role.ExpenseSubmitter,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseSubmitter
      ],
      creditCardButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      approveActionButton: [Role.Admin, Role.ExpenseApprover],
      updatePaymentActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter,
        Role.CustomExp3ExpenseAdmin
      ]
    }
  ],
  [
    'approved_expense_pending_payment',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      duplicateActionButton: [
        Role.Admin,
        Role.ExpenseAdmin,
        Role.ExpenseSubmitter,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseSubmitter
      ],
      creditCardButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      approveActionButton: [Role.Admin, Role.ExpenseApprover],
      updatePaymentActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter,
        Role.CustomExp3ExpenseAdmin
      ]
    }
  ],
  [
    'approved_expense_paid',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      duplicateActionButton: [
        Role.Admin,
        Role.ExpenseAdmin,
        Role.ExpenseSubmitter,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseSubmitter
      ],
      creditCardButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      approveActionButton: [Role.Admin, Role.ExpenseApprover],
      updatePaymentActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter,
        Role.CustomExp3ExpenseAdmin
      ]
    }
  ],
  [
    'approved_expense_unpaid',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      duplicateActionButton: [
        Role.Admin,
        Role.ExpenseAdmin,
        Role.ExpenseSubmitter,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseSubmitter
      ],
      creditCardButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      approveActionButton: [Role.Admin, Role.ExpenseApprover],
      updatePaymentActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter,
        Role.CustomExp3ExpenseAdmin
      ]
    }
  ],
  [
    'submitted_by_me',
    {
      headerPrimaryButton: [
        Role.Admin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      headerExportButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      editActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      viewActionButton: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ],
      duplicateActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseSubmitter
      ],
      creditCardButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ],
      approveActionButton: [Role.Admin, Role.ExpenseApprover],
      updatePaymentActionButton: [
        Role.Admin,
        Role.ExpenseSubmitter,
        Role.ExpenseSubmitter,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter,
        Role.CustomExp3ExpenseAdmin
      ]
    }
  ]
  // [
  //   'sub_grant',
  //   {
  //     headerPrimaryButton: [
  //       Role.Admin,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     headerExportButton: [
  //       Role.Admin,
  //       Role.Auditor,
  //       Role.ReadOnly,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     editActionButton: [
  //       Role.Admin,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     viewActionButton: [
  //       Role.Admin,
  //       Role.Auditor,
  //       Role.ReadOnly,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     duplicateActionButton: [Role.Admin, Role.ExpenseApprover, Role.ExpenseSubmitter]
  //   }
  // ],
  // [
  //   '_sub_grant',
  //   {
  //     headerPrimaryButton: [
  //       Role.Admin,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     headerExportButton: [
  //       Role.Admin,
  //       Role.Auditor,
  //       Role.ReadOnly,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     editActionButton: [
  //       Role.Admin,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     viewActionButton: [
  //       Role.Admin,
  //       Role.Auditor,
  //       Role.ReadOnly,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     duplicateActionButton: [Role.Admin, Role.ExpenseApprover, Role.ExpenseSubmitter]
  //   }
  // ],
  // [
  //   'petty_cash',
  //   {
  //     headerPrimaryButton: [
  //       Role.Admin,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     headerExportButton: [
  //       Role.Admin,
  //       Role.Auditor,
  //       Role.ReadOnly,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     editActionButton: [
  //       Role.Admin,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     viewActionButton: [
  //       Role.Admin,
  //       Role.Auditor,
  //       Role.ReadOnly,
  //       Role.DefaultExpenseApprover,
  //       Role.DefaultExpenseSubmitter,
  //       Role.PayrollExpenseApprover,
  //       Role.PayrollExpenseSubmitter,
  //       Role.CustomExp3ExpenseApprover,
  //       Role.CustomExp3ExpenseSubmitter
  //     ],
  //     duplicateActionButton: [Role.Admin, Role.ExpenseApprover, Role.ExpenseSubmitter]
  //   }
  // ]
]);

const orgInfo = ref<OrgInfoApiResponse | null>(null);
let dynamicRolesInjected = false;

export const preloadDynamicRolesForButton = async () => {
  if (dynamicRolesInjected) return;
  orgInfo.value = await getCachedOrgInfo();

  const categories: any[] = orgInfo.value?.data?.expense_categories_list || [];
  const dynamicRoles: string[] = [];

  for (const category of categories) {
    dynamicRoles.push(
      `${category.value}_expense_submitter`,
      `${category.value}_expense_admin`,
      `${category.value}_expense_approver`
    );
  }

  const targetPaths: string[] = [
    '/expenses',
    '/expenses/add-new-expense',
    '/expenses/edit-expense',
    '/recurring-expenses',
    '/recurring-expenses/add-new-recurring-expense',
    '/recurring-expenses/edit-recurring-expense'
  ];

  const targetActions = [
    'headerPrimaryButton',
    'headerExportButton',
    'editActionButton',
    'viewActionButton',
    'duplicateActionButton',
    'submitForApprovalButton',
    'creditCardButton',
    'approveActionButton',
    'updatePaymentActionButton',
    'cancelActionButton'
  ] as const;

  for (const path of targetPaths) {
    const permission = permissionConfig.get(path);
    if (!permission) continue;

    for (const action of targetActions) {
      if (!permission[action]) continue;
      const roleSet = new Set(permission[action]);
      dynamicRoles.forEach((role: any) => roleSet.add(role));
      permission[action] = Array.from(roleSet);
    }
  }
  dynamicRolesInjected = true;
};

export const preloadDynamicRolesForTabButton = async () => {
  orgInfo.value = await getCachedOrgInfo();
  const categories: any[] = orgInfo.value?.data?.expense_categories_list || [];

  // Add new tabs from categories if they don't exist
  for (const category of categories) {
    const tabName = category.value;
    if (!tabPermissionConfig.has(tabName)) {
      tabPermissionConfig.set(tabName, {
        headerPrimaryButton: [Role.Admin],
        editActionButton: [Role.Admin, Role.ExpenseApprover],
        viewActionButton: [Role.Admin, Role.Auditor, Role.ReadOnly],
        headerExportButton: [Role.Admin, Role.Auditor, Role.ReadOnly],
        duplicateActionButton: [Role.Admin, Role.ExpenseApprover, Role.ExpenseSubmitter],
        submitForApprovalButton: [Role.Admin, Role.ExpenseSubmitter],
        creditCardButton: [Role.Admin, Role.ExpenseSubmitter],
        approveActionButton: [Role.Admin, Role.ExpenseApprover],
        updatePaymentActionButton: [Role.Admin, Role.ExpenseApprover, Role.ExpenseAdmin, Role.ExpenseSubmitter]
      });
    }
  }

  for (const [tabName, tabPermission] of tabPermissionConfig.entries()) {
    const dynamicRoles = [`${tabName}_expense_submitter`, `${tabName}_expense_admin`, `${tabName}_expense_approver`];

    // Add dynamic roles to each action button type
    const targetActions = [
      'headerPrimaryButton',
      'headerExportButton',
      'editActionButton',
      'viewActionButton',
      'duplicateActionButton',
      'submitForApprovalButton',
      'creditCardButton',
      'approveActionButton',
      'updatePaymentActionButton'
    ] as const;

    for (const action of targetActions) {
      if (!tabPermission[action]) {
        tabPermission[action] = [];
      }

      const roleSet = new Set(tabPermission[action]);
      dynamicRoles.forEach((role: any) => roleSet.add(role));
      tabPermission[action] = Array.from(roleSet);
    }
  }
};

// Tabs that are not tied to a single expense type (they list expenses across all types)
const GENERAL_EXPENSE_TABS = [
  'all_expenses',
  'submitted_by_me',
  'pending_approval_by_me',
  'approved_expense_unpaid',
  'approved_expense_paid',
  'approved_expense_pending_payment'
];

// A few expense-category values (e.g. "_project_expenses", "_dept_overheads") carry a leading
// underscore, but the role suffixes issued for them never do (e.g. "project_expenses_expense_admin") —
// see the same normalization in pages/admin/add_new_user.vue. Strip it before building role keys.
// const normalizeExpenseCategory = (category: string): string => category.replace(/^_+/, '');

// "Add New Expense" must only be unlocked by an admin/submitter role for the relevant expense
// type — an approver-only role for that type should keep the button disabled.
function hasAddNewExpenseAccess(userRoles: string[], tab: string, route: string): boolean {
  if (userRoles.includes(Role.Admin)) return true;

  // "all_expenses" tab on /expenses or /recurring-expenses unlocks for any type's admin/submitter
  // role — their add-new pages filter the dropdown themselves. Other general tabs stay Admin-only.
  if (GENERAL_EXPENSE_TABS.includes(tab)) {
    if ((route === '/expenses' || route === '/recurring-expenses') && tab === 'all_expenses') {
      return userRoles.some(r => r.endsWith('_expense_admin') || r.endsWith('_expense_submitter'));
    }
    return false;
  }

  // On an expense-type-specific tab, only that type's admin/submitter role grants access
  const category = tab;
  return userRoles.includes(`${category}_expense_admin`) || userRoles.includes(`${category}_expense_submitter`);
}

export function checkPermission(route: string, action: string, tab?: string): boolean {
  const { role } = useAuth();

  if ((route === '/expenses' || route === '/recurring-expenses') && action === 'headerPrimaryButton') {
    // Right after login/navigation the tab hasn't synced into the URL yet, so `tab` arrives
    // undefined here — default it to "all_expenses" (the page's own default tab) instead of
    // falling through to the legacy, tab-unaware /expenses permission config below.
    return hasAddNewExpenseAccess(role.value, tab || 'all_expenses', route);
  }

  if ((route === '/expenses' || route === '/recurring-expenses') && tab) {
    const hasDynamicAccess = role.value.some(
      r =>
        r.endsWith('admin') ||
        r.endsWith('_expense_admin') ||
        r.endsWith('_expense_submitter') ||
        r.endsWith('_expense_approver')
    );
    if (hasDynamicAccess) {
      return true;
    }
    const tabConfig = tabPermissionConfig.get(tab);
    if (!tabConfig) return false;

    const allowedRoles = tabConfig[action as keyof typeof tabConfig];
    return Array.isArray(allowedRoles) && allowedRoles.some(allowedRole => role.value.includes(allowedRole));
  }

  const routeConfig = permissionConfig.get(route);

  if (!routeConfig) return false;

  const allowedRoles = routeConfig[action as keyof typeof routeConfig];
  return Array.isArray(allowedRoles) && allowedRoles.some(allowedRole => role.value.includes(allowedRole));
}
